import {
  Icon,
  RouteListItemButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAlert,
  mdiAnimation,
  mdiBell,
  mdiCalendar,
  mdiCheck,
  mdiCheckAll,
  mdiCheckCircle,
  mdiFileDocument,
  mdiTruck,
} from "@mdi/js";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Pagination,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute, Link as LinkRoute } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import z from "zod";
import type {
  InfiniteNotificationsData,
  MercureNotificationsResponse,
  Notification,
} from "../../../@types/services/Notification";
import { useAuth } from "../../../hooks/useAuth";
import { subscribeUserMessageChannel } from "../../../services/mercure";
import {
  fetchNotifications,
  fetchUnreadNotificationCounts,
  markAsReadAndSeenNotification,
  markAsReadNotification,
  markAsSeenNotification,
} from "../../../services/notifications";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";

const searchSchema = z.object({
  page: z.number().default(1).catch(1),
  type: z.string().default("all").catch("all"),
});

const loaderQueryOptions = (page: number, type: string) =>
  queryOptions({
    gcTime: 1000 * 60 * 10,
    queryFn: () =>
      fetchNotifications({
        filters: {
          type: type === "all" ? undefined : type,
        },
        page,
      }),
    queryKey: ["notifications", page, type],
    staleTime: 1000 * 60 * 5,
  });

const RouteComponent: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const { t } = useTranslation();

  const { page: currentPage, type: selectedType } = Route.useSearch();

  const navigate = Route.useNavigate();

  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [allUnreadCounts, setAllUnreadCounts] = useState<
    Record<string, number>
  >({});

  const { data: notificationsData, isLoading } = useQuery(
    loaderQueryOptions(currentPage, selectedType),
  );

  const markAsReadMutation = useMutation({
    mutationFn: markAsReadNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
  });

  const markAsSeenMutation = useMutation({
    mutationFn: markAsSeenNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!notificationsData?.results) return;

      const unreadNotifications = notificationsData.results.filter(
        (notification) => !notification.read_at,
      );

      await Promise.all(
        unreadNotifications?.map((notification) =>
          markAsReadAndSeenNotification(notification.id),
        ),
      );
    },
    onSuccess: () => {
      queryClient.setQueryData(
        ["notifications", currentPage, selectedType],
        (
          oldData:
            | { results: Notification[]; total_items?: number }
            | undefined,
        ) => {
          if (!oldData?.results) return oldData;

          return {
            ...oldData,
            results: oldData.results?.map((notification: Notification) => ({
              ...notification,
              read_at: new Date().toISOString(),
              seen_at: notification.seen_at || new Date().toISOString(),
            })),
          };
        },
      );

      setAllUnreadCounts((prevCounts) => {
        const updatedCounts = { ...prevCounts };

        const unreadInCurrentPage =
          notificationsData?.results.filter(
            (notification) => !notification.read_at,
          )?.length || 0;

        updatedCounts.all = Math.max(
          0,
          (updatedCounts.all || 0) - unreadInCurrentPage,
        );

        notificationsData?.results.forEach((notification) => {
          if (!notification.read_at) {
            const typeKey = notification.type || "unknown";
            if (updatedCounts[typeKey]) {
              updatedCounts[typeKey] = Math.max(0, updatedCounts[typeKey] - 1);
            }
          }
        });

        return updatedCounts;
      });
    },
  });

  const { mutate: markAsReadMutate } = markAsReadMutation;
  const { mutate: markAsSeenMutate } = markAsSeenMutation;
  const { mutate: markAllAsReadMutate } = markAllAsReadMutation;

  const handleMessageCallback = useCallback(
    (data: string) => {
      const parsedData = JSON.parse(data) as MercureNotificationsResponse;
      if (
        parsedData.notification_type === "ChatMessageNotifier::Notification"
      ) {
        void queryClient.invalidateQueries({ queryKey: ["notifications"] });
        return;
      }
      queryClient.setQueryData(
        ["notifications", currentPage, selectedType],
        (oldData?: InfiniteNotificationsData) => {
          if (!oldData?.pages) {
            return {
              pageParams: [1],
              pages: [
                {
                  results: [parsedData.notification],
                  total_items: 1,
                },
              ],
            };
          }

          const notificationExists = oldData.pages.some((page) =>
            page.results?.some(
              (notification) => notification.id === parsedData.notification.id,
            ),
          );

          if (notificationExists) {
            return oldData;
          }

          const updatedPages = oldData.pages?.map((page, index) =>
            index === 0
              ? {
                  ...page,
                  results: [parsedData.notification, ...(page.results || [])],
                  total_items: page.total_items + 1,
                }
              : page,
          );

          return {
            ...oldData,
            pages: updatedPages,
          };
        },
      );
    },
    [queryClient, currentPage, selectedType],
  );

  const onMessage = useEffectEvent((data: string) => {
    handleMessageCallback(data);
  });

  useEffect(() => {
    if (!userId) return;

    return subscribeUserMessageChannel(
      { id: userId },
      "notifications",
      (data) => onMessage(data),
    );
  }, [userId]);

  useEffect(() => {
    if (notificationsData?.types_available) {
      const types = notificationsData.types_available;
      setAllTypes(types);
    }
  }, [notificationsData]);

  const fetchAllTypesAndUnread = useCallback(async () => {
    if (!user) return;
    const data = await fetchUnreadNotificationCounts();
    setAllUnreadCounts({
      ...data.unread_by_type,
      all: data.total_unread,
    });
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchAllTypesAndUnread();
    }
  }, [user, fetchAllTypesAndUnread]);

  useEffect(() => {
    if (
      markAllAsReadMutation.isSuccess ||
      markAsReadMutation.isSuccess ||
      markAsSeenMutation.isSuccess
    ) {
      fetchAllTypesAndUnread();
    }
  }, [
    markAllAsReadMutation.isSuccess,
    markAsReadMutation.isSuccess,
    markAsSeenMutation.isSuccess,
    fetchAllTypesAndUnread,
  ]);

  const notificationTypes = useMemo(
    () => (allTypes?.length > 0 ? ["all", ...allTypes] : allTypes),
    [allTypes],
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      navigate({
        search: {
          page: value,
        },
      });
    },
    [navigate],
  );

  const handleMarkAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutate(notificationId);
    },
    [markAsReadMutate],
  );

  const handleMarkAsSeen = useCallback(
    (notificationId: string) => {
      markAsSeenMutate(notificationId);
    },
    [markAsSeenMutate],
  );

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsReadMutate();
  }, [markAllAsReadMutate]);

  const unreadCount = useMemo(
    () =>
      notificationsData?.results?.filter(
        (notification) => !notification.read_at,
      )?.length || 0,
    [notificationsData?.results],
  );

  const typeLabels: Record<string, string> = useMemo(
    () => ({
      "ApprovedDeliveryScheduleNotifier::Notification": t(
        "notifications.typeLabels.ApprovedDeliveryScheduleNotifier",
      ),
      all: t("notifications.typeLabels.all"),
      "CanceledDeliveryScheduleNotifier::Notification": t(
        "notifications.typeLabels.CanceledDeliveryScheduleNotifier",
      ),
      "DeliveryDateChangeRequestCompletedNotifier::Notification": t(
        "notifications.typeLabels.DeliveryDateChangeRequestCompletedNotifier",
      ),
      "DeliveryDateChangeRequestFailedNotifier::Notification": t(
        "notifications.typeLabels.DeliveryDateChangeRequestFailedNotifier",
      ),
      "DeliveryDateChangeRequestPendingApprovalNotifier::Notification": t(
        "notifications.typeLabels.DeliveryDateChangeRequestPendingApprovalNotifier",
      ),
      "DeliveryDateChangeRequestRejectNotifier::Notification": t(
        "notifications.typeLabels.DeliveryDateChangeRequestRejectNotifier",
      ),
      "NewInvoiceApprovalNotifier::Notification": t(
        "notifications.typeLabels.NewInvoiceApprovalNotifier",
      ),
      "NewRequestAnticipationEvaluationQuotationNotifier::Notification": t(
        "notifications.typeLabels.NewRequestAnticipationEvaluationQuotationNotifier",
      ),
      "NewValidationIssueGoodReceiptInvoiceNotifier::Notification": t(
        "notifications.typeLabels.NewValidationIssueGoodReceiptInvoiceNotifier",
      ),
      "PendingDeliveryScheduleNotifier::Notification": t(
        "notifications.typeLabels.PendingDeliveryScheduleNotifier",
      ),
      "ResponseRequestAnticipationEvaluationQuotationNotifier::Notification": t(
        "notifications.typeLabels.ResponseRequestAnticipationEvaluationQuotationNotifier",
      ),
      "SupplierDevolutionGoodReceiptInvoiceNotifier::Notification": t(
        "notifications.typeLabels.SupplierDevolutionGoodReceiptInvoiceNotifier",
      ),
    }),
    [t],
  );

  const typeIcons: Record<string, string> = useMemo(
    () => ({
      "ApprovedDeliveryScheduleNotifier::Notification": mdiCalendar,
      all: mdiBell,
      "CanceledDeliveryScheduleNotifier::Notification": mdiCalendar,
      "DeliveryDateChangeRequestCompletedNotifier::Notification": mdiCalendar,
      "DeliveryDateChangeRequestFailedNotifier::Notification": mdiCalendar,
      "DeliveryDateChangeRequestPendingApprovalNotifier::Notification":
        mdiCalendar,
      "DeliveryDateChangeRequestRejectNotifier::Notification": mdiCalendar,
      "NewInvoiceApprovalNotifier::Notification": mdiFileDocument,
      "NewRequestAnticipationEvaluationQuotationNotifier::Notification":
        mdiAnimation,
      "NewValidationIssueGoodReceiptInvoiceNotifier::Notification": mdiAlert,
      "PendingDeliveryScheduleNotifier::Notification": mdiCalendar,
      "ResponseRequestAnticipationEvaluationQuotationNotifier::Notification":
        mdiCheckCircle,
      "SupplierDevolutionGoodReceiptInvoiceNotifier::Notification": mdiTruck,
    }),
    [],
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, tab: string) => {
      navigate({
        search: {
          page: 1,
          type: tab,
        },
      });
    },
    [navigate],
  );

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: { md: "row", xs: "column" },
        gap: 2,
        justifyContent: "center",
        paddingY: 2,
      }}
    >
      {notificationTypes.length > 0 && !isMobile && (
        <List
          aria-label="list notifications types"
          component={Paper}
          sx={{
            height: "fit-content",
            maxHeight: 600,
            maxWidth: 350,
            minWidth: 315,
            overflow: "auto",
          }}
        >
          {notificationTypes.map((notificationType) => (
            <RouteListItemButtonLink
              key={notificationType}
              search={{
                page: 1,
                type: notificationType,
              }}
              selected={selectedType === notificationType}
              to="/notifications"
            >
              <ListItemIcon>
                <Icon icon={typeIcons[notificationType] || mdiBell} />
              </ListItemIcon>
              <ListItemText
                primary={typeLabels[notificationType] || notificationType}
              />
              {(allUnreadCounts[String(notificationType)] ?? 0) > 0 && (
                <Badge
                  badgeContent={allUnreadCounts[String(notificationType)]}
                  color="primary"
                  max={99}
                />
              )}
            </RouteListItemButtonLink>
          ))}
        </List>
      )}
      <Card>
        <CardHeader
          action={
            unreadCount > 0 && (
              <Button
                data-testid="mark-all-button"
                disabled={markAllAsReadMutation.isPending}
                onClick={handleMarkAllAsRead}
                size="small"
                startIcon={<Icon icon={mdiCheckAll} />}
                variant="outlined"
              >
                {t("notifications.markAllAsRead")}
              </Button>
            )
          }
          title={t("notifications.notifications")}
        />

        {notificationTypes.length > 0 && isMobile && (
          <Tabs
            aria-label="scrollable auto icon tabs notifications types"
            onChange={handleTabChange}
            scrollButtons="auto"
            value={selectedType}
            variant="scrollable"
          >
            {notificationTypes.map((notificationType) => (
              <Tab
                icon={
                  <Badge
                    badgeContent={
                      allUnreadCounts[String(notificationType)] ?? 0
                    }
                    color="primary"
                    key={notificationType}
                    max={99}
                    sx={{
                      alignItems: "center",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Icon
                      fontSize="large"
                      icon={typeIcons[notificationType] || mdiBell}
                    />
                  </Badge>
                }
                key={notificationType}
                label={typeLabels[notificationType] || notificationType}
                value={notificationType}
                wrapped
              />
            ))}
          </Tabs>
        )}

        <CardContent>
          <List>
            {isLoading
              ? Array.from({ length: 5 }).map((_, index) => {
                  const skeletonKey = `skeleton-${index}`;
                  return (
                    <Box key={skeletonKey} sx={{ width: "500px" }}>
                      <ListItem
                        sx={{
                          backgroundColor: "transparent",
                          padding: 0,
                        }}
                      >
                        <Box
                          sx={{
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                            alignItems: "flex-start",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 80,
                            padding: "16px",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              alignItems: "flex-start",
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" />

                              <Skeleton
                                height={16}
                                variant="text"
                                width="100%"
                              />
                              <Skeleton
                                height={16}
                                variant="text"
                                width="85%"
                              />
                              <Skeleton
                                height={14}
                                variant="text"
                                width="45%"
                              />
                            </Box>
                            <Box sx={{ marginLeft: 1 }}>
                              <Skeleton
                                height={24}
                                variant="circular"
                                width={24}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider />
                    </Box>
                  );
                })
              : notificationsData?.results.map((notification, index) => (
                  <Box key={notification.id}>
                    <ListItem
                      sx={{
                        backgroundColor: !notification.read_at
                          ? "action.selected"
                          : "transparent",
                        padding: 0,
                      }}
                    >
                      <ListItemButton
                        component={LinkRoute}
                        onMouseEnter={() => {
                          if (!notification.seen_at) {
                            handleMarkAsSeen(notification.id);
                          }
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                          alignItems: "flex-start",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 80,
                        }}
                        to={notification.url || ""}
                      >
                        <Box
                          sx={{
                            alignItems: "flex-start",
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            {!!notification.title && (
                              <Typography
                                gutterBottom
                                sx={{
                                  color: !notification.read_at
                                    ? "text.primary"
                                    : "text.secondary",
                                  fontWeight: !notification.read_at
                                    ? "bold"
                                    : "normal",
                                }}
                                variant="subtitle1"
                              >
                                {notification.title}
                              </Typography>
                            )}

                            <Typography
                              gutterBottom
                              sx={{ color: "text.secondary" }}
                              variant="body2"
                            >
                              <span
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: The notification content is sanitized in back-end and safe to use
                                dangerouslySetInnerHTML={{
                                  __html: notification.text || "",
                                }}
                              />
                            </Typography>

                            <Typography
                              sx={{ color: "text.secondary" }}
                              variant="caption"
                            >
                              {t(
                                "intlDateTime",
                                i18nextParseToDate(notification.created_at),
                              )}
                            </Typography>
                          </Box>

                          {!notification.read_at && (
                            <Tooltip
                              arrow
                              title={t("notifications.markAsRead")}
                            >
                              <IconButton
                                color="default"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleMarkAsRead(notification.id);
                                }}
                                size="small"
                                sx={{ marginLeft: 1 }}
                              >
                                <Icon icon={mdiCheck} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {index < notificationsData.results.length - 1 && (
                      <Divider />
                    )}
                  </Box>
                ))}
          </List>

          {!isLoading && !notificationsData?.results?.length && (
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                padding: 4,
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                }}
                variant="h6"
              >
                {t("notifications.noNotificationsDescription")}
              </Typography>
            </Box>
          )}

          {!isLoading &&
            notificationsData?.pagination.total_pages &&
            notificationsData?.pagination.total_pages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 2,
                }}
              >
                <Pagination
                  color="primary"
                  count={notificationsData?.pagination.total_pages}
                  data-testid="pagination"
                  onChange={handlePageChange}
                  page={currentPage}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
        </CardContent>
      </Card>
    </Container>
  );
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private/notifications/")({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search: { page, type } }) => ({
    page,
    type,
  }),
  loader: ({ context, deps: { page, type } }) =>
    context.queryClient.ensureQueryData(loaderQueryOptions(page, type)),
});
